import AuthModule from '@modules/AuthModule';
import { STORAGE_KEY_BFF_AUTH_PATH } from '@constants/storage';

/**
 * Performs an authenticated fetch request using the active auth path.
 *
 * Dual-mode: per-request selectAuthPath() determines how auth is sent.
 *
 * Session path — sends credentials:'include' (cookie) with no Authorization
 * header. The server reads the session cookie directly; no token is
 * needed or desirable in the browser.
 *
 * PKCE path — sends Authorization: Bearer <token> as today. credentials is
 * also included (harmless — the session cookie is absent on PKCE).
 *
 * The decision is made per call, not cached at startup, so a kill-switch
 * engaged mid-session takes effect without a page reload.
 *
 * @param input - The resource that you wish to fetch
 * @param init - Optional fetch configuration
 * @returns A Promise that resolves to a Response object
 *
 * @example
 * ```typescript
 * // Fetch a protected resource
 * const response = await authenticatedFetch('/api/recording.mp3');
 * const blob = await response.blob();
 * ```
 *
 * @example
 * ```typescript
 * // POST with custom headers
 * const response = await authenticatedFetch('/api/data', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ data: 'value' })
 * });
 * ```
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  // Check the sticky auth path directly from localStorage — avoids importing
  // authPath.ts (which imports launchdarkly-js-client-sdk) and keeps this
  // module free of LD SDK side-effects that can interfere with test mocks.
  const isSessionPath =
    localStorage.getItem(STORAGE_KEY_BFF_AUTH_PATH) === 'session';

  const headers = new Headers(init?.headers);

  if (isSessionPath) {
    // Session path: cookie carries the credential — no Authorization header.
    return fetch(input, {
      ...init,
      credentials: 'include',
      headers,
    });
  }

  // PKCE path: bearer token as today.
  const token = await AuthModule.getCurrentToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  return fetch(input, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export default authenticatedFetch;
